import { Box, Text, useColorModeValue } from '@chakra-ui/react';
import { OktonSelect } from '@okton/platform-ui';
import React, { useEffect } from 'react';
import { rotuloCaixa, useCaixas } from '../../services/hooks/useCadastros';

interface CaixaFieldProps {
  valor: string;
  onChange: (caixa: string) => void;
  // A data do lançamento: é nela que o Caixa precisa estar aberto.
  data?: string;
  // Em branco some a linha de ajuda — útil dentro da coluna estreita da
  // Conciliação, onde o espaço vertical é medido.
  ajuda?: string;
}

/**
 * Escolha do Caixa em que o lançamento cai.
 *
 * Deixar em branco é o comportamento de sempre: o IFLEX resolve pelo Caixa
 * aberto do usuário, ou pelo Caixa Geral quando ele tem a permissão.
 *
 * A lista traz só os Caixas abertos na data do lançamento. Um Caixa aberto
 * depois dela não recebe o movimento — e antes esta tela deixava escolhê-lo,
 * com o erro só aparecendo na gravação. Quando nenhum serve, o campo diz isso
 * em vez de oferecer uma lista vazia.
 */
const CaixaField: React.FC<CaixaFieldProps> = ({
  valor,
  onChange,
  data,
  ajuda,
}) => {
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const { data: caixas = [], isLoading } = useCaixas(data);
  const semCaixa = !isLoading && caixas.length === 0;

  // Trocar a data pode tirar da lista o Caixa já escolhido: mantê-lo
  // selecionado mandaria ao servidor um Caixa que ele vai recusar.
  useEffect(() => {
    if (valor && !isLoading && !caixas.find(c => c.id_cxa === valor)) {
      onChange('');
    }
  }, [caixas, isLoading, valor, onChange]);

  return (
    <Box>
      <Text fontSize="sm" fontWeight="medium" mb="1">
        Caixa
      </Text>
      <OktonSelect
        size="lg"
        rounded="2xl"
        name="caixa"
        color={color}
        value={valor}
        isDisabled={semCaixa}
        onChange={e => onChange(e.target.value)}
      >
        <option value="">
          {semCaixa
            ? 'Nenhum Caixa aberto no período'
            : 'Caixa aberto (padrão do IFLEX)'}
        </option>
        {caixas.map(c => (
          <option key={c.id_cxa} value={c.id_cxa}>
            {rotuloCaixa(c)}
          </option>
        ))}
      </OktonSelect>
      {semCaixa ? (
        <Text fontSize="xs" color="red.500" mt="1">
          Não há Caixa aberto na data deste lançamento. Abra um Caixa no IFLEX
          para essa data antes de gravar.
        </Text>
      ) : (
        !!ajuda && (
          <Text fontSize="xs" color="gray.500" mt="1">
            {ajuda}
          </Text>
        )
      )}
    </Box>
  );
};

export default CaixaField;
